<template>
  <TableList
    v-rect-loading="loading"
    noAction
    class="applicant-lock-config-list"
    row-key="_id"
    :listData="configList"
    :title="$t(`${i18nPrefix}.title`)"
    :tableColumns="tableColumns"
  >
    <template #filter>
      <el-select
        v-model="search.companyId"
        filterable
        class="company-filter"
        @change="searchCompanyList"
      >
        <el-option-group
          v-for="(group, index) in groupedCompanyList"
          :key="index"
          :label="group.label"
        >
          <el-option
            v-for="item in group.value"
            :key="item.id"
            :label="`${item.id} - ${item.i18n_name}`"
            :style="{ paddingLeft: item.level * 15 + 'px' }"
            :value="item.id"
          />
        </el-option-group>
      </el-select>
    </template>
    <template #btn>
      <el-button v-show="isDelete" class="create-group-btn create-btn" @click="deletedConfig">{{ $t(`${i18nPrefix}.btn.deleteConfig`) }}</el-button>
      <el-button v-show="isSave" class="create-group-btn create-btn" @click="saveConfig">{{ $t(`${i18nPrefix}.btn.saveConfig`) }}</el-button>
      <el-button v-show="isAdd" class="create-group-btn create-btn" @click="addConfig">{{ $t(`${i18nPrefix}.btn.addConfig`) }}</el-button>
      <el-button v-show="isAddAndSave" class="create-group-btn create-btn" @click="saveConfig">{{ $t(`${i18nPrefix}.btn.saveConfig`) }}</el-button>
    </template>
    <template #remindConfigHeader>
      <span>{{ $t(`${i18nPrefix}.label.remindConfig`) }}</span>
      <span v-auto-tooltip.always="$t(`${i18nPrefix}.message.remindConfigRuleTip`)" class="tip-message-icon">
        <i class="el-icon-question"></i>
      </span>
    </template>
    <template #lockTime="{ row }">
      <el-select v-model="row.lock_type" :class="row.error.lock_type && 'has-error'">
        <el-option
          v-for="(item, index) in LOCK_TYPE"
          :key="index"
          :label="$t(`${i18nPrefix}.lockType.${item}`)"
          :value="item"
        ></el-option>
      </el-select>
      <el-input-number
        v-model="row.lock_time"
        size="medium"
        controls-position="right"
        step-strictly
        :step="1"
        :class="row.error.lock_time && 'has-error'"
        :disabled="!row.lock_type || row.lock_type !== LOCK_TYPE.DAY_LOCK"
        :placeholder="$t(`${i18nPrefix}.placeholder.lockTime`)"
        :min="0"
        @change="onLockTimeChange($event, row)"
      ></el-input-number>
    </template>
    <template #remindConfig="{ row }">
      <el-input
        v-model="row.reminder_config"
        clearable
        size="small"
        :class="row.error.reminder_config && 'has-error'"
        :disabled="!row.lock_type || row.lock_type === LOCK_TYPE.RELEASE"
        :placeholder="$t(`${i18nPrefix}.placeholder.remindConfig`)"
        @change="onRemindConfigChange($event, row)"
      >
      </el-input>
    </template>
    <template #page-bottom>
      <DialogWrapper
        :title="$t(`${i18nPrefix}.message.confirmDelete`)"
        :show.sync="dialogVisible"
        :loading="dialogLoading"
        @save="handleConfirmDelete"
      >
      </DialogWrapper>
    </template>
  </TableList>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import { addI18nProperty } from '@/i18n'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'

const LOCK_TYPE = {
  RELEASE: 'RELEASE',
  DAY_LOCK: 'DAY_LOCK',
  INFINITY_LOCK: 'INFINITY_LOCK'
}

export default {
  components: {
    TableList,
    DialogWrapper
  },
  data () {
    return {
      loading: false,
      LOCK_TYPE,
      i18nPrefix: 'admin.company.applicantLock',
      companyList: [],
      configList: [],
      search: {
        companyId: null
      },
      isDelete: true,
      isSave: true,
      isAdd: false,
      isAddAndSave: false,
      dialogVisible: false,
      dialogLoading: false
    }
  },
  computed: {
    tableColumns () {
      const columns = [
        {
          label: this.$t(`${this.i18nPrefix}.label.status`),
          dataKey: 'i18n_name'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.lockTime`),
          dataKey: 'lock_time',
          slotName: 'lockTime'
        },
        {
          width: 60,
          label: this.$t(`${this.i18nPrefix}.label.remindConfig`),
          dataKey: 'reminder_config',
          headerSlotName: 'remindConfigHeader',
          slotName: 'remindConfig'
        }
      ]
      return columns
    },
    groupedCompanyList () {
      const vm = this
      return _.chain(this.companyList)
        .groupBy(company => {
          return company.has_lock_config ? 'has' : 'not'
        })
        .mapValues((group, i18nKey) => {
          const companyList = []
          const orderCompany = _.orderBy(group, ['level'], ['asc'])
          _.forEach(orderCompany, item => {
            if (_.find(companyList, { id: item.id })) {
              return
            }
            companyList.push(item)
            companyList.push(...vm.formatCompanies(group, item.id, item.level))
          })
          return {
            i18nKey,
            label: vm.$t(`${vm.i18nPrefix}.label.hasConfig.${i18nKey}`),
            value: companyList
          }
        })
        .sortBy('i18nKey').value()
    }
  },
  created () {
    this.getCompanyList()
  },
  methods: {
    ...mapActions('resumeLockConfig', [
      'getCompanyListCanConfig',
      'getApplicantLockConfig',
      'deleteApplicantLockConfig',
      'updateApplicantLockConfig',
      'createApplicantLockConfig'
    ]),
    async getCompanyList (companyId) {
      this.loading = true
      try {
        const resp = await this.getCompanyListCanConfig()
        if (resp) {
          this.companyList = _.map(resp.company_list, company => {
            addI18nProperty(company, 'i18n_name', 'cn_name', 'name')
            return company
          })
          if (!companyId) {
            this.search.companyId = this.groupedCompanyList[0].value[0].id
          } else {
            this.search.companyId = companyId
          }
          await this.searchCompanyList()
        }
      } finally {
        this.loading = false
      }
    },
    async searchCompanyList () {
      this.loading = true
      try {
        if (!this.search.companyId) {
          return
        }
        const resp = await this.getApplicantLockConfig({ companyId: this.search.companyId })
        if (resp) {
          const configList = resp.company_applicant_lock_config.applicant_lock_config
          const hasSetConfig = _.some(configList, item => Boolean(item.lock_type))
          if (hasSetConfig) {
            this.configList = _.map(configList, config => {
              addI18nProperty(config, 'i18n_name', 'name_cn', 'name_en')
              config.error = {
                lock_type: false,
                lock_time: false,
                reminder_config: false
              }
              return config
            })
            this.isDelete = true
            this.isSave = true
            this.isAdd = false
          } else {
            this.isDelete = false
            this.isSave = false
            this.isAdd = true
            this.configList = []
          }
        }
      } finally {
        this.loading = false
      }
    },
    async addConfig () {
      this.loading = true
      try {
        if (!this.search.companyId) {
          return
        }
        const resp = await this.getApplicantLockConfig({ companyId: this.search.companyId })
        if (resp) {
          this.configList = _.map(resp.company_applicant_lock_config.applicant_lock_config, config => {
            addI18nProperty(config, 'i18n_name', 'name_cn', 'name_en')
            config.error = {
              lock_type: false,
              lock_time: false,
              reminder_config: false
            }
            return config
          })
        }
        this.isAdd = false
        this.isSave = false
        this.isDelete = false
        this.isAddAndSave = true
      } finally {
        this.loading = false
      }
    },
    validateConfig (config, noErrorTip, allowEmpty) {
      const error = config.error
      _.forEach(Object.keys(error), (key) => {
        error[key] = false
      })
      const lockType = config.lock_type
      const lockTime = config.lock_time
      const remindConfig = config.reminder_config
      const errorMessage = []
      if (allowEmpty && !lockType && !lockTime && !remindConfig) {
        return false
      }
      if (!lockType) {
        errorMessage.push(this.$t(`${this.i18nPrefix}.message.lockTypeIsRequired`))
        error.lock_type = true
      } else if (lockType === LOCK_TYPE.RELEASE) {
        config.lock_time = 0
        config.reminder_config = null
      } else if (lockType === LOCK_TYPE.INFINITY_LOCK) {
        if (!_.isInteger(_.toNumber(remindConfig))) {
          errorMessage.push(this.$t(`${this.i18nPrefix}.message.remindConfigIsNotInt`))
          error.reminder_config = true
        }
      } else if (lockType === LOCK_TYPE.DAY_LOCK) {
        if (!lockTime || !_.isInteger(lockTime)) {
          errorMessage.push(this.$t(`${this.i18nPrefix}.message.lockTimeIsNotInt`))
          error.lock_time = true
        }
        if (remindConfig) {
          if (/^[0-9]*([0-9]+\+?)*[0-9]$/.test(remindConfig)) {
            const remindDays = _.chain(remindConfig).split('+').map(item => parseInt(item)).sum().value()
            if (remindDays > lockTime) {
              error.lock_time = true
              error.reminder_config = true
              errorMessage.push(this.$t(`${this.i18nPrefix}.message.remindDaysLessThanLockDays`))
            }
          } else {
            error.reminder_config = true
            errorMessage.push(this.$t(`${this.i18nPrefix}.message.invalidRemindConfig`))
          }
        }
      }
      if (errorMessage.length > 0 && !noErrorTip) {
        this.$message({
          type: 'error',
          dangerouslyUseHTMLString: true,
          message: _.map(errorMessage, item => `<div>${item}</div>`).join('')
        })
      }
      return errorMessage.length > 0
    },
    async saveConfig () {
      if (!this.search.companyId) {
        return
      }
      this.loading = true
      let success = false
      try {
        const configList = this.configList
        let isInvalid = false
        let hasSet = false
        _.forEach(configList, config => {
          if (this.validateConfig(config, true, true)) {
            isInvalid = true
          }
          const lockType = config.lock_type
          if (lockType) {
            hasSet = true
          }
        })
        if (!hasSet) {
          this.$message.error(this.$t(`${this.i18nPrefix}.message.cannotSaveEmpty`))
          return
        }
        if (isInvalid) {
          this.$message.error(this.$t(`${this.i18nPrefix}.message.checkConfigList`))
          this.$nextTick(() => {
            this.$el.querySelector('.el-table .has-error').scrollIntoView({ block: 'center', behavior: 'smooth' })
          })
          return
        }
        const saveFunc = this.isAddAndSave ? this.createApplicantLockConfig : this.updateApplicantLockConfig
        const resp = await saveFunc({ companyId: this.search.companyId, configList })
        if (resp) {
          success = true
          this.isAddAndSave = false
        }
        await this.getCompanyList(this.search.companyId)
      } finally {
        this.loading = false
        if (success) {
          this.$message.success(this.$t('common.message.saveSuccess'))
        }
      }
    },
    onRemindConfigChange (remindConfig, config) {
      config.reminder_config = remindConfig
      this.validateConfig(config)
    },
    onLockTimeChange (lockTime, config) {
      config.lock_time = lockTime
      this.validateConfig(config)
    },
    async handleConfirmDelete () {
      if (!this.search.companyId) {
        return
      }
      this.dialogLoading = true
      let success = false
      try {
        const resp = await this.deleteApplicantLockConfig(this.search.companyId)
        if (resp) {
          success = true
          this.dialogVisible = false
          await this.getCompanyList(this.search.companyId)
        }
      } finally {
        this.dialogLoading = false
        if (success) {
          this.$message.success(this.$t(`${this.i18nPrefix}.message.deleteSuccess`))
        }
      }
    },
    deletedConfig () {
      this.dialogVisible = true
    },
    formatCompanies (companies, parentId) {
      const children = _.filter(companies, item => item.parent_id === parentId)
      return _.flatMapDeep(children, child => {
        const descendants = this.formatCompanies(companies, child.id)
        return [child, descendants]
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.applicant-lock-config-list {
  .filter-group {
    .company-filter {
      width: 50%;
    }
  }

  .tip-message-icon {
    margin-left: 5px;
    font-size: 14px;
  }

  ::v-deep {
    .el-table {
      .has-error {
        .el-input__inner,
        .el-input__inner:focus {
          border-color: #f56c6c;
        }
      }

      .el-select {
        width: 50%;
        margin-right: 10px;

        .el-input__inner {
          padding: 0 20px 0 5px;
          text-align: center;
        }

        .el-input__suffix {
          right: 0;
        }

        .el-input__icon {
          line-height: 28px;
          width: 22px;
        }
      }

      .el-input-number {
        .el-input-number__decrease {
          border-left: 1px solid #c0c4cc;
        }

        .el-input-number__increase {
          border-bottom: 1px solid #c0c4cc;
        }

        &.is-disabled {
          .el-input-number__decrease {
            border-left: 1px solid #e4e7ed;
          }

          .el-input-number__increase {
            border-bottom: 1px solid #e4e7ed;
          }
        }
      }

      .el-input__inner {
        height: 34px;
        line-height: 34px;
      }
    }
  }
}
</style>
